/**
 * Lets an administrator select Box folders for one durable connector feed.
 *
 * Navigation and OAuth stay browser-local; the selected folder identity is the
 * only provider state persisted by DocReview's shared connector lifecycle.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronRight, RefreshCw, Search } from 'lucide-react'
import { toast } from 'sonner'

import {
  Badge,
  Button,
  Checkbox,
  Input,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@/components/ui'
import { FolderGlyph } from '@/subsystems/vault/components/VaultItemIcon'
import { filterAndSortBoxEntries, type BoxSort } from '../services/box-browser'
import { listBoxFolder, type BoxEntry } from '../services/source-api'

export function BoxFolderBrowser({
  token,
  selected,
  onSelectedChange,
}: {
  token: string
  selected: BoxEntry[]
  onSelectedChange: (selected: BoxEntry[]) => void
}) {
  const [folders, setFolders] = useState([{ id: '0', name: 'All Files' }])
  const [entries, setEntries] = useState<BoxEntry[]>([])
  const [query, setQuery] = useState('')
  const [sort, setSort] = useState<BoxSort>('updated-desc')
  const [loading, setLoading] = useState(false)
  const currentFolder = folders[folders.length - 1]

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      setEntries(await listBoxFolder(token, currentFolder.id))
    } catch (error) {
      toast.error((error as Error).message)
    } finally {
      setLoading(false)
    }
  }, [currentFolder.id, token])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const visible = useMemo(
    () => filterAndSortBoxEntries(entries, query, sort, 'files')
      .filter(entry => entry.type === 'folder'),
    [entries, query, sort],
  )
  const openFolder = (next: { id: string; name: string }[]) => {
    setFolders(next)
    setQuery('')
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-48 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Filter this folder"
            aria-label="Filter Box folder"
            className="h-8 pl-8 text-xs"
          />
        </div>
        <Select value={sort} onValueChange={value => setSort(value as BoxSort)}>
          <SelectTrigger className="h-8 w-40 text-xs" aria-label="Sort Box folders">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="updated-desc">Updated: newest</SelectItem>
            <SelectItem value="name-asc">Name: A–Z</SelectItem>
            <SelectItem value="name-desc">Name: Z–A</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label="Refresh Box folder"
          disabled={loading}
          onClick={() => void refresh()}
        >
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} />
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <nav aria-label="Box folder path" className="flex min-w-0 flex-1 flex-wrap items-center gap-0.5 text-xs">
          {folders.map((folder, index) => (
            <span key={`${folder.id}-${index}`} className="flex items-center gap-0.5">
              {index > 0 && <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />}
              {index === folders.length - 1 ? (
                <span className="max-w-48 truncate font-medium">{folder.name}</span>
              ) : (
                <button
                  type="button"
                  className="max-w-40 truncate rounded px-1 py-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={() => openFolder(folders.slice(0, index + 1))}
                >
                  {folder.name}
                </button>
              )}
            </span>
          ))}
        </nav>
        <Badge variant={selected.length ? 'default' : 'outline'}>
          {selected.length ? `${selected.length} selected` : 'None selected'}
        </Badge>
      </div>

      <ScrollArea className="min-h-0 flex-1 rounded-lg border border-slate-200 bg-white">
        {visible.map(entry => {
          const checked = selected.some(folder => folder.id === entry.id)
          return (
            <div key={entry.id} className={cn('flex items-center gap-2.5 border-b border-slate-100 px-3 py-2 last:border-0 hover:bg-slate-50', checked && 'bg-slate-50')}>
              <Checkbox
                checked={checked}
                aria-label={`Select ${entry.name}`}
                onCheckedChange={value => onSelectedChange(
                  value
                    ? [...selected, entry]
                    : selected.filter(folder => folder.id !== entry.id),
                )}
              />
              <FolderGlyph className="h-[18px] w-[18px] shrink-0" />
              <button
                type="button"
                className="flex min-w-0 flex-1 items-center text-left text-xs text-slate-700 hover:text-slate-950"
                onClick={() => openFolder([...folders, { id: entry.id, name: entry.name }])}
              >
                <span className="truncate">{entry.name}</span>
                <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
              </button>
            </div>
          )
        })}
        {!loading && !visible.length && (
          <p className="p-8 text-center text-sm text-muted-foreground">
            No folders match this filter.
          </p>
        )}
      </ScrollArea>
    </div>
  )
}
